import React from "react";

/**
 * SectionDivider — subtle decorative separators inspired by traditional
 * Mexican geometric patterns (greca escalonada). Used sparingly to add
 * brand identity without visual clutter.
 *
 * Variants:
 *  - "greca": three stepped diamonds with thin connecting lines (between sections)
 *  - "mark": single diamond between two short lines (hero / footer accents)
 *  - "papel": wider papel-picado-inspired silhouette (footer only)
 */

const Greca = ({ tone = "light", className = "" }) => {
  const stroke = tone === "dark" ? "#F97316" : "#F97316";
  const lineOpacity = tone === "dark" ? 0.55 : 0.6;
  return (
    <div className={`flex items-center justify-center ${className}`} role="presentation" aria-hidden="true">
      <svg
        width="200"
        height="14"
        viewBox="0 0 200 14"
        fill="none"
        stroke={stroke}
        strokeWidth="1"
        strokeLinejoin="miter"
      >
        <line x1="0" y1="7" x2="60" y2="7" opacity={lineOpacity} />
        <path d="M66 7 L73 0.5 L80 7 L73 13.5 Z" />
        <path d="M89 7 L100 0.5 L111 7 L100 13.5 Z" />
        <path d="M120 7 L127 0.5 L134 7 L127 13.5 Z" />
        <line x1="140" y1="7" x2="200" y2="7" opacity={lineOpacity} />
      </svg>
    </div>
  );
};

const Mark = ({ tone = "light", className = "" }) => {
  const stroke = tone === "dark" ? "#F97316" : "#F97316";
  return (
    <div className={`flex items-center justify-center gap-4 ${className}`} role="presentation" aria-hidden="true">
      <span className="w-12 h-px" style={{ background: stroke, opacity: 0.55 }} />
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
        <path d="M5 0 L10 5 L5 10 L0 5 Z" stroke={stroke} strokeWidth="1" />
      </svg>
      <span className="w-12 h-px" style={{ background: stroke, opacity: 0.55 }} />
    </div>
  );
};

/* Papel — Aztec stepped fret (greca escalonada) silhouette, single horizontal band */
const Papel = ({ tone = "dark", className = "" }) => {
  const stroke = "#F97316";
  return (
    <div className={`flex items-center justify-center ${className}`} role="presentation" aria-hidden="true">
      <svg
        width="320"
        height="16"
        viewBox="0 0 320 16"
        fill="none"
        stroke={stroke}
        strokeWidth="1"
      >
        {/* Left baseline */}
        <line x1="0" y1="8" x2="70" y2="8" opacity="0.45" />
        {/* Stepped fret — left mirror */}
        <path d="M76 8 L76 4 L84 4 L84 0 L92 0 L92 4 L100 4 L100 8" />
        {/* Center diamond */}
        <path d="M120 8 L135 0 L150 8 L135 16 Z" />
        {/* Stepped fret — right */}
        <path d="M170 8 L170 4 L178 4 L178 0 L186 0 L186 4 L194 4 L194 8" />
        {/* Right baseline */}
        <line x1="200" y1="8" x2="320" y2="8" opacity="0.45" />
      </svg>
    </div>
  );
};

const SectionDivider = ({ variant = "greca", tone = "light", className = "", "data-testid": testId }) => {
  const baseProps = { tone, className, "data-testid": testId };
  if (variant === "mark") return <div data-testid={testId}><Mark {...baseProps} /></div>;
  if (variant === "papel") return <div data-testid={testId}><Papel {...baseProps} /></div>;
  return <div data-testid={testId}><Greca {...baseProps} /></div>;
};

export default SectionDivider;
